<!-- svelte-ignore a11y-label-has-associated-control -->
<!-- svelte-ignore a11y-missing-attribute -->

<div class="title has-text-centered">Your charity</div>
<div class="subtitle has-text-centered">Registered in England &amp; Wales</div>
{#if !charity}
  <div class="field">
    <label class="label">Charity name or number</label>
    <div class="control">
      <input
        class="input"
        type="text"
        placeholder="Start typing to find your charity"
        bind:value={name}
        bind:this={inputElement}
        on:input={onChangeName}
      />
    </div>
  </div>

  {#if results}
    {#if results.length > 0}
      <div in:fade class="block">
        <p class="mt-3">
          Select your charity {#if searching}<span
              class="has-text-grey-light">(updating...)</span
            >{/if}
        </p>

        {#each results as result}
          <p class="mt-4">
            <a on:click={() => select(result)}>
              {result.name}
            </a>
          </p>
        {/each}

        <p class="mt-3">
          Not shown? Enter more of the name, or try your registered
          charity number.
        </p>
      </div>
    {:else if searching}
      <div class="block">
        <p class="mt-3 has-text-grey-light">
          Finding matching charities...
        </p>
      </div>
    {:else}
      <div in:fade class="block">
        <p class="mt-3">
          Sorry, we can't find a matching charity. Try your registered
          charity number.
        </p>
        <p>
          If you're still having problems, let us know at <a
            href="mailto:hello@agoodthing.org.uk"
            >hello@agoodthing.org.uk</a
          > and we'll help get you setup.
        </p>
      </div>
    {/if}
  {:else if searching}
    <div class="block">
      <p class="mt-3 has-text-grey-light">
        Finding matching charities...
      </p>
    </div>
  {/if}
{:else}
  <div class="block">
    <div class="field">
      <label class="label">Charity name</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Required"
          bind:value={charity.name}
        />
      </div>
    </div>

    <div class="field">
      <label class="label">Charity number</label>
      <div class="control">
        <input
          disabled
          class="input"
          type="text"
          placeholder=""
          value={charityNumber}
        />
      </div>
    </div>

    <div class="field">
      <label class="label">Website</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Optional"
          bind:value={charity.website}
        />
      </div>
    </div>

    <div class="field">
      <label class="label">Postcode</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Required"
          bind:value={charity.contact.postcode}
        />
      </div>
    </div>
  </div>
{/if}

<div class="block">
  <button
    disabled={!isValid}
    class="button is-primary"
    class:is-loading={isLoading}
    on:click={submit}>Submit</button
  >
  {#if charity}
    <button class="button" on:click={reset}>Search again</button>
  {/if}
</div>

{#if errorMessage}
  <div class="block">
    <div class="notification is-danger is-light">
      {errorMessage}
    </div>
  </div>
{/if}

<div class="block"><a href={$url('/start/charity/type')}>&lt; Select a different type of charity</a></div>


<script lang="ts">
  import { goto, url } from "@sveltech/routify";
  import { logging } from '../../../../lib/logging';
  import { appState } from "../../../../lib/appState";
  import { fade } from "svelte/transition";

  import _ from "lodash";
  
  let charityNumber = '';

  let isLoading = false;
  let errorMessage : string;

  let name : string;
  let inputElement : any;
  let results : any[];
  let searching = false;

  let charity : Charity_v2 | undefined;

  $: isValid =
    charity != undefined && charity.name && charityNumber && charity.contact.postcode;

  function setResults(_results :any[], searchTerm :string) {
    // check it is not out of date (the current search term)
    if (searchTerm == inputElement.value) {
      results = _results;
    }
  }

  const onChangeName = _.debounce(async (event :any) => {
    setResults(await findCharities(event.target.value), event.target.value);
  }, 500);

  async function findCharities(searchTerm: string) {
    if (searchTerm.length > 2) {
      searching = true;

      let queries = searchTerm.split(/\W+/).map((term) => ({
        name: { $regex: term, $options: "i" },
      }));

      const res = await fetch('https://do.agoodthing.org.uk/api/charities', {
        method: 'POST',
        body: JSON.stringify({
          find: {
            $or: [{ $and: queries }, { charityNumber: searchTerm }],
          },
          //find: {$text: { $search: searchTerm}},
          //sort: { score: { $meta: "textScore" } },
          limit: 10,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      searching = false;

      return (await res.json()).results;
    } else {
      return undefined;
    }
  }

  async function getCharity(name: string) {
    const res = await fetch('https://do.agoodthing.org.uk/api/charity', {
      method: 'POST',
      body: JSON.stringify({
        name,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await res.json();
  }

  async function select(item :any) {
    let charityInfo : CharityInfo = await getCharity(item.name);

    charityNumber = charityInfo.charityNumber;

    charity = {
      type: 'EnglandAndWalesRegistered',
      name: charityInfo.name,
      location: charityInfo.location,
      website: charityInfo.website,
      contact: charityInfo.contact,
      description: charityInfo.activities,
      domains: charityInfo.domains,
      image: charityInfo.image,
      info: {
        name: charityInfo.name,
        charityNumber: charityInfo.charityNumber,
        activities: charityInfo.activities,
        areaOfBenefit: charityInfo.areaOfBenefit,
        operations: charityInfo.operations,
        numPeople: charityInfo.numPeople,
        registrations: charityInfo.registrations,
        beneficiaries: charityInfo.beneficiaries,
        causes: charityInfo.causes,
        names: charityInfo.names
      }
    }
  }

  async function reset() {
    charity = undefined;
  }

  async function submit() {
    isLoading = true;

    try {
      await window.firebase
        .functions()
        .httpsCallable('api-account-createcharity')({ charity } as CreateCharityParams)
        .then((result) => {
          $appState.user!.charity = result.data.id;

          isLoading = false;
          $goto('../../ready');
        });
    } catch (err: any) {
      logging.report(err.toString());
      errorMessage = err.toString();
    }

    isLoading = false;
  }

</script>
